/**
 * useCreateApplication Hook
 *
 * React Query mutation for creating a new application.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { applicationService } from '../../../api/application.service';
import type {
  ApplicationSanitized,
  ApplicationCreateDTO,
} from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseCreateApplicationOptions {
  tenantId: string;
  onSuccess?: (data: ApplicationSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for creating a new application
 */
export const useCreateApplication = (
  options: UseCreateApplicationOptions
): UseMutationResult<ApplicationSanitized, Error, ApplicationCreateDTO> => {
  const { tenantId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<ApplicationSanitized, Error, ApplicationCreateDTO>({
    mutationFn: async (data: ApplicationCreateDTO) => {
      console.log('[useCreateApplication] Creating application:', data.name);
      return await applicationService.createApplication(tenantId, data);
    },
    onSuccess: (data) => {
      console.log('[useCreateApplication] Application created successfully:', data.applicationId);
      // Invalidate applications list query
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useCreateApplication] Failed to create application:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
